import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import store from 'stores/RootStore';
import { AnimationPanelViews } from 'types/AnimationPanelViews';
import {
  dragHelper,
  eventDataTransferHasFiles,
  makeSingleFileFilter,
} from 'utils/DragHelper';

import AnimationStage from './AnimationStage/AnimationStage';
import CanvasDrawer from './Drawers/CanvasDrawer';
import CommentsDrawer from './Drawers/CommentsDrawer/CommentsDrawer';
import ExportDrawer from './Drawers/ExportDrawer/ExportDrawer';
import UploadVersionModal from './Drawers/VersionsDrawer/UploadVersionModal';
import VersionsDrawer from './Drawers/VersionsDrawer/VersionsDrawer';

// This file has significant repetition of
// client/src/components/Views/Creative/Creative.tsx
// See there for explainers and caveats.

const Container = styled.div.attrs({
  className: 'animation-root',
})`
  display: flex;
  flex-direction: row;
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

function isJsonFile(file: File) {
  return file.type === 'application/json' || /\.json$/i.test(file.name);
}

const dragFileFilter = makeSingleFileFilter(isJsonFile);

function Animation() {
  const store_ui = store.ui;
  const store_current = store.current;
  const store_animation = store.animation;

  const displayPanelBeforeDrag = useRef(AnimationPanelViews.NONE);

  useEffect(() => {
    if (store_current?.data.name) {
      document.title = store_current.data.name;
    }
  }, [store_current?.data.name]);

  useEffect(() => {
    if (!store_animation) return;

    return dragHelper({
      target: window,
      preFilter: eventDataTransferHasFiles,
      // Note that .lottie is not accepted because 'lottie-web' (the AirBnB
      // player) is used to play back animations, which has no awareness of
      // the .lottie format, being a lottiefiles.com invention (which
      // incidentally, is a PKZIP of the JSON and referenced resources).
      // Currently the `@dotlottie/dotlottie-js` library is used to *create*
      // .lottie files.
      filter: dragFileFilter,
      dropEffect: 'copy',
      onFirstDragEnter: () => {
        store_ui.fileDragging = true;
        displayPanelBeforeDrag.current =
          store_animation.displayPanel || AnimationPanelViews.NONE;
        store_animation.setPanel(AnimationPanelViews.VERSIONS);
      },
      onLastDragLeave: () => {
        store_ui.fileDragging = false;
        store_animation.setPanel(displayPanelBeforeDrag.current);
      },
      onDropAccepted: (event) => {
        store_ui.fileDragging = false;
        // When accepting the drop, don't restore the former panel
        displayPanelBeforeDrag.current = AnimationPanelViews.VERSIONS;
        store_animation.setPanel(AnimationPanelViews.VERSIONS);

        const [theFile] = event.dataTransfer!.files;
        void store_animation.uploadAnimationFile(theFile);
      },
      onDragEnd: () => {
        store_ui.fileDragging = false;
        store_animation.setPanel(displayPanelBeforeDrag.current);
      },
    });
  }, [store_animation, store_ui]);

  // One idiosyncrasy here that departs from Creative.tsx: calling .reset is
  // not strictly necessary due to how Animation.reset differs from
  // Creative.reset. But the old class component called it so I've kept it.
  useEffect(() => {
    return () => {
      store_animation?.reset();
    };
  }, [store_animation]);

  return (
    <>
      {store_animation && (
        <Container>
          <AnimationStage />

          <VersionsDrawer />
          <CommentsDrawer />
          <ExportDrawer />
          <CanvasDrawer />

          <UploadVersionModal />
        </Container>
      )}
    </>
  );
}

export default observer(Animation);
